import React, { useEffect, useMemo, useState } from "react";
import { FormControl, Modal } from "react-bootstrap";
import {
  HiCheckCircle,
  HiGlobeAlt,
  HiMagnifyingGlass,
  HiMiniMapPin,
} from "react-icons/hi2";
import { STRIPE_SUPPORTED_COUNTRIES } from "../constants/stripeCountries";

const getFlagEmoji = (code) =>
  code.replace(/./g, (char) => String.fromCodePoint(127397 + char.charCodeAt()));

const StripeCountrySelectModal = ({ open, onClose, onConfirm, loading }) => {
  const [selected, setSelected] = useState(null);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!open) {
      setSelected(null);
      setQuery("");
    }
  }, [open]);

  const filteredCountries = useMemo(() => {
    const keyword = query.trim().toLowerCase();
    if (!keyword) return STRIPE_SUPPORTED_COUNTRIES;

    return STRIPE_SUPPORTED_COUNTRIES.filter(
      (country) =>
        country.name.toLowerCase().includes(keyword) ||
        country.code.toLowerCase().includes(keyword)
    );
  }, [query]);

  const selectedCountry = useMemo(
    () => STRIPE_SUPPORTED_COUNTRIES.find((country) => country.code === selected),
    [selected]
  );

  const handleClose = () => {
    if (!loading) onClose();
  };

  const handleConfirm = () => {
    if (selected && !loading) onConfirm(selected);
  };

  return (
    <Modal
      show={open}
      onHide={handleClose}
      centered
      className="stripe-region-modal"
      backdrop={loading ? "static" : true}
      keyboard={!loading}
    >
      <Modal.Header closeButton={!loading} className="stripe-region-modal__header">
        <div className="stripe-region-modal__header-content">
          <div className="stripe-region-modal__icon-wrap" aria-hidden="true">
            <HiGlobeAlt size={22} />
          </div>
          <div>
            <Modal.Title className="stripe-region-modal__title">
              Select your country
            </Modal.Title>
            <p className="stripe-region-modal__subtitle mb-0">
              Choose a Stripe-supported country for your business. We&apos;ll send
              that country code to Stripe onboarding automatically.
            </p>
          </div>
        </div>
      </Modal.Header>

      <Modal.Body className="stripe-region-modal__body">
        <div className="stripe-region-modal__search-wrap">
          <HiMagnifyingGlass className="stripe-region-modal__search-icon" size={18} />
          <FormControl
            type="text"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search by country name or code"
            className="stripe-region-modal__search-input"
            disabled={loading}
            aria-label="Search Stripe-supported countries"
          />
        </div>

        <div className="stripe-region-modal__select-shell">
          <label
            htmlFor="stripe-country-select"
            className="stripe-region-modal__select-label"
          >
            Stripe-supported countries
          </label>
          <select
            id="stripe-country-select"
            value={selected || ""}
            onChange={(event) => setSelected(event.target.value || null)}
            disabled={loading}
            className="stripe-region-modal__select"
          >
            <option value="">Choose your country</option>
            {filteredCountries.map((country) => (
              <option key={country.code} value={country.code}>
                {country.name} ({country.code})
              </option>
            ))}
          </select>
        </div>

        {selectedCountry ? (
          <div className="stripe-region-modal__selected-card" aria-live="polite">
            <div className="stripe-region-modal__selected-flag" aria-hidden="true">
              {getFlagEmoji(selectedCountry.code)}
            </div>
            <div className="stripe-region-modal__selected-copy">
              <div className="stripe-region-modal__selected-topline">
                <span className="stripe-region-modal__selected-name">
                  {selectedCountry.name}
                </span>
                <span className="stripe-region-modal__selected-code">
                  {selectedCountry.code}
                </span>
              </div>
              <p className="stripe-region-modal__selected-text mb-0">
                Your Stripe account will be created with country code{" "}
                <strong>{selectedCountry.code}</strong>.
              </p>
            </div>
            <HiCheckCircle
              className="stripe-region-modal__selected-check"
              size={22}
              aria-hidden="true"
            />
          </div>
        ) : (
          <div className="stripe-region-modal__empty-state">
            <HiMiniMapPin size={18} aria-hidden="true" />
            <span>Select a country to continue to Stripe onboarding.</span>
          </div>
        )}

        {query && filteredCountries.length === 0 && (
          <p className="stripe-region-modal__empty-search mb-0">
            No Stripe-supported countries match your search.
          </p>
        )}

        <p className="stripe-region-modal__note">
          This affects your payout setup and compliance requirements. If your
          business country isn&apos;t listed here, Stripe Connect isn&apos;t available
          for it in this flow.
        </p>
      </Modal.Body>

      <Modal.Footer className="stripe-region-modal__footer">
        <button
          type="button"
          className="stripe-region-modal__btn stripe-region-modal__btn--ghost"
          onClick={handleClose}
          disabled={loading}
        >
          Cancel
        </button>
        <button
          type="button"
          className="stripe-region-modal__btn stripe-region-modal__btn--primary"
          onClick={handleConfirm}
          disabled={!selected || loading}
        >
          {loading ? "Connecting..." : "Continue to Stripe"}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default StripeCountrySelectModal;
